import moment from "moment";
import { MAX_OVERTIME_MULTIPLIER } from "../../../components/plan/StatMap/interactiveStatMap";
import ICleaningSchedule from "../../../models/cleaningSchedule";
import IMiscleanedLocalsDisplay from "../../../models/globalDashboard/miscleanedLocalsDisplay";
import IEmployeeTimesheetOverview from "../../../models/globalDashboard/userTimesheetOverview";
import { ILocalDetail } from "../../../models/requests/local/localDetail";
import { IProcessedScan } from "../../../models/stat";
import { IStackedBarChartProps } from "../../../pages/dashboard/chart/global/StackedBarChart";
import DateUtils from "../../../utils/dateUtils";

interface IMiscleanedLocals {
  date: Date;
  local: ILocalDetail;
  minTime: number;
  scannedTime: number;
}

export const getProductivity = (workedHours: number, globalHours: number) => {
  if (globalHours <= 0) return undefined;
  return Math.min(100, Math.max(0, Math.round((workedHours / globalHours) * 100)));
};

export const getPercentageChange = (current?: number, previous?: number) => {
  if (!previous) return undefined;
  if (!current && current !== 0) return undefined;

  return ((current - previous) / previous) * 100;
};

export const getTotalHours = (scans: IProcessedScan[], previousScans: IProcessedScan[]) => {
  const totalMinutes = scans.reduce((acc, scan) => acc + scan.minutes, 0);
  const totalHours = DateUtils.convertMinutesToHours(totalMinutes);

  const previousMinutes = previousScans.reduce((acc, scan) => acc + scan.minutes, 0);
  const previousHours = DateUtils.convertMinutesToHours(previousMinutes);

  return { totalHours, previousHours };
};

export const getWorkedHoursByBuilding = (localScans: IProcessedScan[]): IStackedBarChartProps => {
  const groupedByBuilding: Record<string, Record<string, number>> = {};

  localScans.forEach((scan) => {
    const buildingName = scan.start.buildingName;
    const userName = scan.start.username;
    const minutes = scan.minutes;

    if (!groupedByBuilding[buildingName]) {
      groupedByBuilding[buildingName] = {};
    }

    if (!groupedByBuilding[buildingName][userName]) {
      groupedByBuilding[buildingName][userName] = 0;
    }

    groupedByBuilding[buildingName][userName] += minutes;
  });

  const categories = Object.keys(groupedByBuilding);
  const series: { name: string; data: number[] }[] = [];

  const users = new Set<string>();
  categories.forEach((building) => {
    Object.keys(groupedByBuilding[building]).forEach((user) => users.add(user));
  });

  users.forEach((user) => {
    series.push({
      name: user,
      data: categories.map((building) => DateUtils.convertMinutesToHours(groupedByBuilding[building][user] || 0)),
    });
  });

  return { categories, series };
};

export const getGlobalHoursByDay = (inOutScans: IProcessedScan[]) => {
  const weekdays = moment.weekdaysShort(true).map((day) => day.toLowerCase());
  const hoursByDay: Record<string, number> = weekdays.reduce((acc, day) => ({ ...acc, [day]: 0 }), {});

  inOutScans.forEach((scan) => {
    const day = moment(scan.start.date).format("ddd").toLowerCase();
    if (hoursByDay[day] !== undefined) {
      hoursByDay[day] += scan.minutes;
    }
  });

  Object.keys(hoursByDay).forEach((key) => {
    hoursByDay[key] = DateUtils.convertMinutesToHours(hoursByDay[key]);
  });

  const categories = Object.keys(hoursByDay);
  const data = Object.values(hoursByDay);

  return { categories, data };
};

export const getWorkedHoursRatioByBuilding = (localScans: IProcessedScan[]) => {
  const totalMinutes = localScans.reduce((acc, scan) => acc + scan.minutes, 0);

  const groupedByBuilding: Record<string, number> = {};
  localScans.forEach((scan) => {
    const buildingName = scan.start.buildingName;
    if (!groupedByBuilding[buildingName]) {
      groupedByBuilding[buildingName] = 0;
    }

    groupedByBuilding[buildingName] += scan.minutes;
  });

  const labels = Object.keys(groupedByBuilding);
  const series = labels.map((label) => Number((groupedByBuilding[label] / totalMinutes * 100).toFixed(1)));

  return { labels, series };
};

export const getMiscleanedLocals = (start: Date, end: Date, localScans: IProcessedScan[], cleaningSchedules: ICleaningSchedule[], locals: ILocalDetail[]) => {
  const nonVisitedLocals: IMiscleanedLocals[] = [];
  const insufficientlyCleanedLocals: IMiscleanedLocals[] = [];
  const overCleanedLocals: IMiscleanedLocals[] = [];

  const scansByLocalIdAndDate: Record<number, Record<string, number>> = {};
  localScans.forEach((scan) => {
    const localId = scan.start.localId;
    const date = moment(scan.start.date).format("YYYY-MM-DD");

    if (!scansByLocalIdAndDate[localId]) {
      scansByLocalIdAndDate[localId] = {};
    }

    if (!scansByLocalIdAndDate[localId][date]) {
      scansByLocalIdAndDate[localId][date] = 0;
    }

    scansByLocalIdAndDate[localId][date] += scan.minutes;
  });

  const startMoment = moment(start);
  const endMoment = moment(end);

  while (startMoment.isBefore(endMoment) || startMoment.isSame(endMoment)) {
    const currentDay = startMoment.locale("en").format("dddd").toLowerCase() as keyof ICleaningSchedule;
    const currentDate = startMoment.format("YYYY-MM-DD");

    cleaningSchedules.forEach((schedule) => {
      const localId = schedule.localId;
      const minTime = schedule[currentDay];
      const scannedTime = scansByLocalIdAndDate[localId]?.[currentDate] || 0;

      if (minTime <= 0) return;

      const local = locals.find((l) => l.local.id === localId);
      if (!local) return;
      if (local.local.isLocked) return;

      if (scannedTime <= 0) return nonVisitedLocals.push({ date: startMoment.toDate(), local, minTime, scannedTime });
      if (scannedTime < minTime) return insufficientlyCleanedLocals.push({ date: startMoment.toDate(), local, minTime, scannedTime });
      if (scannedTime > minTime * MAX_OVERTIME_MULTIPLIER) return overCleanedLocals.push({ date: startMoment.toDate(), local, minTime, scannedTime });
    });

    startMoment.add(1, "day");
  }

  return { nonVisitedLocals, insufficientlyCleanedLocals, overCleanedLocals };
};

export const getMiscleanedLocalsDisplay = (localCleanings: IMiscleanedLocals[]): IMiscleanedLocalsDisplay[] => {
  return localCleanings.sort((a, b) => moment(b.date).diff(moment(a.date))).map((cleaningRecord, index) => ({
    id: index,
    date: moment(cleaningRecord.date).format("DD MMM yyyy"),
    building: cleaningRecord.local.building.name,
    floor: cleaningRecord.local.floor.name,
    name: cleaningRecord.local.local.name,
    requiredTime: Number(cleaningRecord.minTime.toFixed(1)),
    actualTime: Number(cleaningRecord.scannedTime.toFixed(1)),
  }));
};

export const getWorkedHoursRatioByProfile = (localScans: IProcessedScan[]) => {
  const totalMinutes = localScans.reduce((acc, scan) => acc + scan.minutes, 0);

  const groupedByProfile: Record<string, number> = {};
  localScans.forEach((scan) => {
    const profileName = scan.start.profileName;
    if (!groupedByProfile[profileName]) {
      groupedByProfile[profileName] = 0;
    }

    groupedByProfile[profileName] += scan.minutes;
  });

  const labels = Object.keys(groupedByProfile);
  const series = labels.map((label) => Number((groupedByProfile[label] / totalMinutes * 100).toFixed(1)));

  return { labels, series };
};

export const getTop10FloorsByWorkedHours = (localScans: IProcessedScan[]) => {
  const NUMBER_OF_FLOORS = 10;
  const hoursByFloor: Record<string, number> = {};

  localScans.forEach((scan) => {
    const floorName = scan.start.floorName;
    if (!hoursByFloor[floorName]) {
      hoursByFloor[floorName] = 0;
    }

    hoursByFloor[floorName] += scan.minutes;
  });

  Object.keys(hoursByFloor).forEach((key) => {
    hoursByFloor[key] = DateUtils.convertMinutesToHours(hoursByFloor[key]);
  });

  const categories = Object.keys(hoursByFloor).filter(key => hoursByFloor[key]).sort((a, b) => hoursByFloor[b] - hoursByFloor[a]).slice(0, NUMBER_OF_FLOORS);
  const data = Object.values(hoursByFloor).filter(value => value).sort((a, b) => b - a).slice(0, NUMBER_OF_FLOORS);

  return { categories, data };
};

export const getEmployeesTimesheetOverview = (
  scans: { inOutScans: IProcessedScan[]; localScans: IProcessedScan[] },
  previousIntervalScans: { inOutScans: IProcessedScan[]; localScans: IProcessedScan[] }
) => {
  const timesheetOverview: IEmployeeTimesheetOverview[] = [];
  const employeeIds = scans.localScans.map((scan) => scan.start.userId);
  const employeeIdsSet = new Set(employeeIds);

  employeeIdsSet.forEach((employeeId) => {
    const employeeScans = {
      inOutScans: scans.inOutScans.filter((scan) => scan.start.userId === employeeId),
      localScans: scans.localScans.filter((scan) => scan.start.userId === employeeId),
      previousInOutScans: previousIntervalScans.inOutScans.filter((scan) => scan.start.userId === employeeId),
      previousLocalScans: previousIntervalScans.localScans.filter((scan) => scan.start.userId === employeeId),
    }

    const { totalHours: globalHours, previousHours: previousGlobalHours } = getTotalHours(employeeScans.inOutScans, employeeScans.previousInOutScans);
    const { totalHours: workedHours, previousHours: previousWorkedHours } = getTotalHours(employeeScans.localScans, employeeScans.previousLocalScans);
    const productivity = getProductivity(workedHours, globalHours);

    const globalPercentageChange = getPercentageChange(globalHours, previousGlobalHours);
    const workedPercentageChange = getPercentageChange(workedHours, previousWorkedHours);
    const productivityPercentageChange = getPercentageChange(productivity, getProductivity(previousWorkedHours, previousGlobalHours));

    timesheetOverview.push({
      id: employeeId,
      username: scans.localScans.find((scan) => scan.start.userId === employeeId)?.start.username || "",
      globalHours,
      workedHours,
      productivity,
      globalPercentageChange,
      workedPercentageChange,
      productivityPercentageChange,
    });
  });

  timesheetOverview.sort((a, b) => a.username.localeCompare(b.username));

  return timesheetOverview;
};